import React, { useCallback, useEffect, useState } from 'react';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import Modal from '../ui/Modal';
import DayStrip from './DayStrip';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';
import Timeline from './Timeline';
import { formatHealthTime, formatHealthFull, resolveTz, tzHeaderLabel } from '../../utils/healthTime';
import { healthStatusVariant, tileStateClass, liveHeadlineBadge } from '../../utils/healthStyle';
import {
  describeKey, describeHeadline, describeReason, describeTileReason, statusLabel, TILE_NAME, tileStateLabel,
  COUNTER_LABEL, ACTION_LABEL,
} from '../../config/healthText';

const unwrap = (res) => res?.data?.data ?? res?.data ?? null;

/** Device-detail "Health" section. Reports the loaded health up via onLoaded. */
export default function HealthSection({ deviceId, onLoaded }) {
  const { showToast } = useGlobal();
  const [health, setHealth] = useState(null);
  const [error, setError]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = (initial) => adminApi.get(`/device/${deviceId}/health`)
      .then((res) => { if (!cancelled) { const h = unwrap(res); setHealth(h); setError(false); onLoaded?.(h); } })
      .catch(() => { if (!cancelled && initial) setError(true); })
      .finally(() => { if (!cancelled && initial) setLoading(false); });
    load(true);
    const t = setInterval(() => load(false), 30000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const action = health?.suggested_action;
  const runAction = useCallback(async () => {
    if (!action?.command) return;
    setSending(true);
    try {
      await adminApi.post(`/device/${deviceId}/command`, { command: action.command });
      showToast('success', 'Command Sent', `${action.command} dispatched.`);
      setConfirm(false);
    } catch {
      showToast('error', 'Error', 'Command failed.');
    } finally { setSending(false); }
  }, [action, deviceId, showToast]);

  if (loading) return <div className="card-padded mb-6 flex justify-center py-6"><Spinner /></div>;
  if (error || !health) {
    return <div className="card-padded mb-6 text-sm text-text-muted">Health data could not be loaded.</div>;
  }

  const isLegacy = health.legacy || health.status === 'legacy';
  const tz = resolveTz(health.timezone);
  const ctx = { tz, now: Date.now() };
  const headline = describeHeadline(health.headline, ctx) || describeReason(health.reason, ctx);
  const liveBadge = liveHeadlineBadge(health.headline);
  const actionLabel = action?.action ? (ACTION_LABEL[action.action] ?? action.action) : null;

  return (
    <div className="card-padded mb-6" data-testid="health-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-text-main">Health</h3>
          <p className="text-[11px] text-text-muted" data-testid="health-tz">Times in {tzHeaderLabel(tz)}</p>
        </div>
        <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1">
          {[['overview', 'Overview'], ['log', 'Technical log']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1 rounded-md text-xs font-medium ${tab === k ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {tab === 'log' ? <TechnicalLog deviceId={deviceId} tz={tz} /> : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={liveBadge ? liveBadge.variant : healthStatusVariant(health.status)} dot>{liveBadge ? liveBadge.label : statusLabel(health.status)}</Badge>
            <span className="text-sm text-text-main" data-testid="health-headline">{headline}</span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            {health.since && !liveBadge ? <span title={formatHealthFull(health.since, tz)}>Since {formatHealthTime(health.since, tz) || '—'}</span> : null}
            {health.since && !liveBadge && ' · '}
            <span title={formatHealthFull(health.last_signal_at, tz)}>Last signal: {formatHealthTime(health.last_signal_at, tz) || '—'}</span>
          </p>
          {isLegacy && (
            <p className="text-xs text-gold mt-2">
              This device runs an old app version and sends no health data — the tiles and counters below are not available.
            </p>
          )}

          {action && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-text-muted">Suggested: <span className="text-text-main">{describeKey(action.key, action.params ?? {}, ctx)}</span></span>
              {action.action && action.command && (
                <button className="btn-secondary text-xs" onClick={() => setConfirm(true)}>{actionLabel}</button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {['connection', 'audio', 'picture', 'app'].map((k) => {
              const t = health.tiles?.[k] ?? { state: 'unknown' };
              return (
                <div key={k} className="rounded-xl border border-white/[0.08] p-3">
                  <p className="text-xs uppercase tracking-wide text-text-muted">{TILE_NAME[k]}</p>
                  <p className={`text-sm font-semibold mt-1 ${tileStateClass(t.state)}`}>{tileStateLabel(t.state)}</p>
                  <p className="text-xs text-text-muted mt-1">{describeTileReason(k, t.reason, ctx)}</p>
                </div>
              );
            })}
          </div>

          {!isLegacy && health.counters_today && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-xs text-text-muted" data-testid="counters-today">
              <span>Today:</span>
              {Object.keys(COUNTER_LABEL).map((k) => (
                <span key={k}>{COUNTER_LABEL[k]}: <strong className="text-text-main">{health.counters_today[k] ?? 0}</strong></span>
              ))}
            </div>
          )}

          <h4 className="text-sm font-semibold text-text-main mt-5 mb-2">Timeline</h4>
          <Timeline timeline={health.timeline} timelineDays={health.timeline_days} ctx={ctx} />
          {(health.info_reasons ?? []).map((r, i) => (
            <p key={i} className="text-xs text-text-muted mt-2" data-testid="info-reason">{describeKey(r.key, r.params, ctx)}</p>
          ))}

          <h4 className="text-sm font-semibold text-text-main mt-5 mb-2">Last 7 days</h4>
          <DayStrip days={health.days} legend />
        </>
      )}

      <Modal open={confirm} onClose={() => setConfirm(false)} title={actionLabel ?? 'Confirm'} size="sm"
        footer={<>
          <button className="btn-secondary" onClick={() => setConfirm(false)} disabled={sending}>Cancel</button>
          <button className="btn-primary" onClick={runAction} disabled={sending}>{sending ? 'Sending…' : 'Confirm'}</button>
        </>}>
        <p className="text-text-muted text-sm">
          {actionLabel}: send the “{action?.command}” command to this device now?
        </p>
      </Modal>
    </div>
  );
}

const CATEGORIES = ['AUDIO', 'VIDEO', 'NETWORK', 'APP_LIFECYCLE', 'SCHEDULED_FEATURE', 'CONTENT', 'ACTIONS'];
const SEVERITIES = ['info', 'warning', 'error', 'critical'];

export function TechnicalLog({ deviceId, tz }) {
  const [faultsOnly, setFaultsOnly] = useState(true);
  const [rows, setRows] = useState([]);
  const [pg, setPg] = useState(null);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [severity, setSeverity] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.get(`/device/${deviceId}/health/events`, {
      params: { page, per_page: 25, kind: faultsOnly ? ['FAULT', 'RECOVERY', 'CONTEXT'] : undefined, category: category || undefined, template_key: templateKey.trim() || undefined, severity: severity || undefined },
    })
      .then((res) => { if (!cancelled) { const d = unwrap(res); setRows(d?.data ?? []); setPg(d?.pagination ?? null); setError(false); } })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deviceId, page, category, templateKey, severity, faultsOnly]);

  // "Faults only" is a SERVER filter (kind[]=FAULT,RECOVERY,CONTEXT) so pages and
  // totals only count the rows shown.
  const shown = rows;
  const reset = (setter) => (e) => { setter(e.target.value); setPage(1); };
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-3">
        <select className="input-base py-1.5 text-xs w-auto" value={category} onChange={reset(setCategory)}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input-base py-1.5 text-xs w-auto" value={severity} onChange={reset(setSeverity)}>
          <option value="">All severities</option>
          {SEVERITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input-base py-1.5 text-xs w-48" placeholder="template_key…" value={templateKey} onChange={reset(setTemplateKey)} />
        <label className="inline-flex items-center gap-1.5 text-xs text-text-muted cursor-pointer">
          <input type="checkbox" checked={faultsOnly} onChange={(e) => { setFaultsOnly(e.target.checked); setPage(1); }} data-testid="faults-only" />
          Faults only
        </label>
      </div>
      {loading ? <div className="flex justify-center py-6"><Spinner /></div>
        : error ? <p className="text-sm text-text-muted">Could not load events.</p>
        : shown.length === 0 ? <p className="text-sm text-text-muted">{faultsOnly ? 'No fault events match — turn off "Faults only" to see informational events.' : 'No events match.'}</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-text-muted">
                <th className="py-1 pr-3">Time</th><th className="pr-3">Category</th><th className="pr-3">Severity</th><th className="pr-3">Event</th><th>App</th>
              </tr></thead>
              <tbody>
                {shown.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-white/[0.05] cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      <td className="py-1 pr-3 whitespace-nowrap">{formatHealthFull(r.occurred_at, tz)}</td>
                      <td className="pr-3">{r.category}</td>
                      <td className="pr-3">{r.severity}</td>
                      <td className="pr-3 text-text-main">{describeKey(r.template_key, r.params, { tz })}</td>
                      <td>{r.app_version ?? '—'}</td>
                    </tr>
                    {expanded === r.id && (
                      <tr><td colSpan={5}>
                        <pre className="input-base text-[11px] overflow-auto max-h-40 m-1">{JSON.stringify({ template_key: r.template_key, subtype: r.subtype, correlation_id: r.correlation_id, received_at: formatHealthFull(r.received_at, tz), params: r.params }, null, 2)}</pre>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {pg && pg.total_pages > 1 && (
        <div className="flex items-center gap-3 mt-3 text-xs text-text-muted">
          <button className="btn-secondary text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {pg.current_page} / {pg.total_pages} · {pg.total} events</span>
          <button className="btn-secondary text-xs" disabled={page >= pg.total_pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
